import { applyPriceMultiplier } from './helpers';

export function generatePrintHTML(list, options = {}) {
  const { 
    withAisles = false, 
    organizedData = null, 
    selectedSupermarket = null 
  } = options;

  const statusLabels = { 
    pending: "Pendiente", 
    completed: "Completada", 
    received: "Recibida", 
    converted: "Convertida a pedido" 
  };

  const useOrganized = withAisles && organizedData?.organized_items?.length > 0;
  const priceTier = selectedSupermarket?.price_tier ?? null;

  const aislesSectionHtml = useOrganized
    ? organizedData.organized_items.map((aisle) => `
    <div class="aisle-block">
      <div class="aisle-title">
        ${aisle.aisle_number ? `<span class="aisle-num">Pasillo ${aisle.aisle_number}</span>` : ""}
        ${aisle.emoji} ${aisle.aisle}
      </div>
      <table>
        <thead><tr>
          <th style="width:36px">#</th>
          <th>Ingrediente</th>
          <th style="width:140px">Cantidad</th>
          <th style="width:130px; text-align:right">Precio estimado</th>
        </tr></thead>
        <tbody>
          ${aisle.items.map((item, i) => {
            const adj = priceTier ? applyPriceMultiplier(item.estimated_cost, priceTier) : item.estimated_cost;
            const showOrig = priceTier && priceTier !== 3 && item.estimated_cost != null;
            return `<tr>
              <td>${i + 1}</td>
              <td>${item.ingredient_name}</td>
              <td>${item.quantity} ${item.unit}</td>
              <td class="num">${adj != null ? adj.toFixed(2) + " €" : "—"}${showOrig ? `<br><span style="font-size:10px;color:#999;text-decoration:line-through">${item.estimated_cost.toFixed(2)} €</span>` : ""}</td>
            </tr>`;
          }).join("")}
        </tbody>
      </table>
    </div>`).join("")
    : `<table>
    <thead><tr>
      <th style="width:36px">#</th>
      <th>Ingrediente</th>
      <th style="width:140px">Cantidad</th>
      <th style="width:130px; text-align:right">Coste estimado</th>
    </tr></thead>
    <tbody>
      ${list.items.map((item, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${item.ingredient_name}</td>
        <td>${item.quantity} ${item.unit}</td>
        <td class="num">${item.estimated_cost != null ? item.estimated_cost.toFixed(2) + " €" : "—"}</td>
      </tr>`).join("")}
    </tbody>
  </table>`;

  const totalEstimated = (() => {
    if (useOrganized && priceTier && priceTier !== 3) {
      const allItems = organizedData.organized_items.flatMap(a => a.items);
      const adjTotal = allItems.reduce((s, item) => s + applyPriceMultiplier(item.estimated_cost ?? 0, priceTier), 0);
      return adjTotal.toFixed(2) + " €";
    }
    return (list.total_estimated_cost?.toFixed(2) ?? "0.00") + " €";
  })();

  return `<!DOCTYPE html>
<html lang="es"><head>
  <meta charset="utf-8">
  <title>${list.name}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; padding: 32px; color: #111; font-size: 13px; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; border-bottom: 2px solid #111; padding-bottom: 16px; }
    .title { font-size: 22px; font-weight: bold; margin-bottom: 4px; }
    .meta { color: #555; font-size: 12px; }
    .badge { display: inline-block; padding: 2px 10px; border-radius: 99px; font-size: 11px; background: #f0f0f0; border: 1px solid #ddd; }
    .aisle-block { margin-bottom: 20px; break-inside: avoid; }
    .aisle-title { font-size: 15px; font-weight: bold; background: #f4f4f4; padding: 8px 12px; border-left: 4px solid #e85d04; margin-bottom: 4px; border-radius: 0 4px 4px 0; display: flex; align-items: center; gap: 8px; }
    .aisle-num { background: #e85d04; color: white; font-size: 11px; font-weight: 700; padding: 1px 7px; border-radius: 99px; }
    .supermarket-badge { display: inline-flex; align-items: center; gap: 6px; background: #fff4ef; border: 1px solid #e85d04; color: #e85d04; padding: 3px 10px; border-radius: 99px; font-size: 11px; font-weight: 600; margin-top: 6px; }
    table { width: 100%; border-collapse: collapse; margin-top: 2px; }
    thead th { background: #f7f7f7; padding: 8px 12px; text-align: left; border-bottom: 2px solid #ddd; font-size: 12px; text-transform: uppercase; letter-spacing: 0.05em; }
    tbody td { padding: 9px 12px; border-bottom: 1px solid #eee; }
    tbody tr:last-child td { border-bottom: none; }
    td.num { text-align: right; font-variant-numeric: tabular-nums; }
    .footer { margin-top: 24px; display: flex; justify-content: space-between; align-items: center; border-top: 2px solid #111; padding-top: 12px; }
    .total { font-size: 18px; font-weight: bold; }
    .total span { color: #e85d04; }
    @media print { body { padding: 0; } .aisle-block { break-inside: avoid; } }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="title">${list.name}</div>
      <div class="meta">${list.forecast_period ? `Periodo: ${list.forecast_period} · ` : ""}${list.items.length} productos</div>
      ${useOrganized && organizedData.supermarket ? `<div class="supermarket-badge">🏪 Organizado para: ${organizedData.supermarket}</div>` : ""}
    </div>
    <div style="text-align:right">
      <div class="badge">${statusLabels[list.status] || list.status}</div>
      <div class="meta" style="margin-top:6px">${new Date(list.created_at).toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" })}</div>
    </div>
  </div>
  ${aislesSectionHtml}
  <div class="footer">
    <div class="meta">${useOrganized ? `${organizedData.organized_items.length} pasillos` : `${list.items.length} productos`}${selectedSupermarket ? ` · ${selectedSupermarket.name}` : ""}</div>
    <div class="total">Total estimado: <span>${totalEstimated}</span></div>
  </div>
</body></html>`;
}

export function openPrintWindow(html) {
  const w = window.open("", "_blank", "width=860,height=700");
  if (!w) return;
  const doc = w.document;
  doc.open();
  doc.write(html);
  doc.close();
  w.focus();
  setTimeout(() => { w.print(); }, 300);
}
